import * as XLSX from 'xlsx';
import { ALL_USAGES, USAGES, ENERGIES, cloneRatios, isValidDjclim, isValidDju, isValidRatio, periodsOf, referencePeriodOf, setUsageRatio, usageRatio } from './domain';
import { isValidPerf, perfOf, perfTableOf, setPerf } from './perf';
import { parseClipboard, parseNum } from './sheet';
import type { DegreeDays, RatioTable } from './types';

export interface ImportResult {
  degrees: DegreeDays;
  ratios: RatioTable;
  /** Nombre de valeurs de ratios reprises. */
  applied: number;
  coefficientsApplied: number;
  /** Lignes non reconnues (typologie inconnue, valeur hors plage…). */
  skipped: string[];
  djuFound: boolean;
  djclimFound: boolean;
}

const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

const USAGE_BY_LABEL = new Map(ALL_USAGES.map((u) => [norm(u.label), u.key]));
USAGE_BY_LABEL.set(norm('Résidentiel collectif'), 'resCollectif');
USAGE_BY_LABEL.set(norm('Résidentiel individuel'), 'resIndividuel');
USAGE_BY_LABEL.set(norm('Enseignement'), 'enseignement');
USAGE_BY_LABEL.set(norm('Autre'), 'autre');

/**
 * Lit un export tableur de ratios (CSV ou TSV).
 * Lignes reconnues : `DJU;2500`, `DJClim;110`, puis `Typologie;Chauffage;ECS;Froid`.
 */
export function parseRatioFile(text: string, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  let degrees = { ...base.degrees };
  let ratios = cloneRatios(base.ratios);
  let applied = 0;
  let djuFound = false;
  let djclimFound = false;
  const skipped: string[] = [];

  for (const cells of parseClipboard(text)) {
    const head = norm(cells[0] ?? '');
    if (head === '' && cells.length <= 1) continue;

    if (head === 'dju' || head === 'djuchauffage' || head === 'mondju') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDju(n)) { degrees = { ...degrees, dju: n }; djuFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }
    if (head === 'djclim' || head === 'djclimfroid' || head === 'mondjclim') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDjclim(n)) { degrees = { ...degrees, djclim: n }; djclimFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }

    const usage = USAGE_BY_LABEL.get(head);
    if (!usage) {
      if (head !== 'typologie' && head !== '') skipped.push(cells.join(' ; '));
      continue;
    }
    ENERGIES.forEach((e, i) => {
      const raw = cells[i + 1];
      if (raw === undefined || raw.trim() === '') return;
      const n = parseNum(raw);
      if (!isValidRatio(n)) { skipped.push(`${cells[0]} — ${e.label} : ${raw}`); return; }
      if (n !== usageRatio(ratios, usage)[e.key]) ratios = setUsageRatio(ratios, usage, e.key, n);
      applied += 1;
    });
  }

  return { degrees, ratios, applied, coefficientsApplied: 0, skipped, djuFound, djclimFound };
}

export function parseRatioWorkbook(data: ArrayBuffer, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  const workbook = XLSX.read(data, { type: 'array', cellNF: true });
  if (workbook.Sheets.Contexte?.A1?.v === 'Format' || workbook.Sheets['Paramètres']) {
    if (!workbook.Sheets.Contexte) referenceWorkbookError('Contexte', 'A1', 'conservez la feuille technique du modèle');
    return parseReferenceWorkbook(workbook, base);
  }
  const sheet = workbook.Sheets.Ratios ?? workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('Le classeur ne contient aucune feuille de ratios.');
  let startingRatios = cloneRatios(base.ratios);
  const context = workbook.Sheets.Contexte;
  if (context) {
    const rows = XLSX.utils.sheet_to_json<string[]>(context, { header: 1, raw: false, defval: '' });
    const schema = rows.find(row => row[0] === 'Périodes')?.[1];
    if (schema !== 'legacy' && schema !== 'climate2026') throw new Error('Schéma de périodes inconnu. Aucune valeur importée.');
    startingRatios = { ...startingRatios, periodSchema: schema, climateZone: rows.find(row => row[0] === 'Zone climatique')?.[1] || undefined };
    const performance = perfTableOf(startingRatios);
    for (const u of ALL_USAGES) for (const p of periodsOf(startingRatios)) {
      startingRatios[u.key][p.key] = { chauffage: null, ecs: null, froid: null };
      const percent = p.key === referencePeriodOf(startingRatios) ? 100 : null;
      performance[u.key][p.key] = { chauffage: percent, ecs: percent, froid: percent };
    }
    startingRatios.performance = performance;
  }
  const result = parseRatioFile(XLSX.utils.sheet_to_csv(sheet, { FS: ';', blankrows: false, rawNumbers: true }), { ...base, ratios: startingRatios });
  const PERIODS = periodsOf(result.ratios);
  const REF_PERIOD = referencePeriodOf(result.ratios);
  const coefficientSheets = ENERGIES.map(e => `Coefficients ${e.key}`);
  if (!context && !coefficientSheets.some(name => workbook.Sheets[name])) return result;
  if (!coefficientSheets.every(name => workbook.Sheets[name])) {
    throw new Error('Les trois feuilles de coefficients Chauffage, ECS et Froid sont requises ensemble. Aucune valeur importée.');
  }
  for (const e of ENERGIES) {
    const rows = XLSX.utils.sheet_to_json<(string | number)[]>(workbook.Sheets[`Coefficients ${e.key}`], { header: 1, raw: true, defval: '' }).map(row => row.map(String));
    if (rows[0]?.join('|') !== ['Typologie', ...PERIODS.map(p => p.label)].join('|')) {
      throw new Error(`Colonnes invalides dans la feuille Coefficients ${e.key}. Aucune valeur importée.`);
    }
    const seen = new Set<string>();
    for (const row of rows.slice(1)) {
      if (row.every(cell => cell.trim() === '')) continue;
      const u = USAGE_BY_LABEL.get(norm(row[0] ?? ''));
      if (!u || seen.has(u)) throw new Error(`Typologie inconnue ou dupliquée dans Coefficients ${e.key}. Aucune valeur importée.`);
      seen.add(u);
      for (const [i, p] of PERIODS.entries()) {
        const raw = row[i + 1] ?? '';
        if (raw.trim() === '') continue;
        const n = parseNum(raw);
        const existing = perfOf(base.ratios, u, p.key, e.key);
        // Une ancienne valeur hors plage reste réimportable à l'identique, jamais corrigée en silence.
        if ((p.key === REF_PERIOD && n !== 100) || (!isValidPerf(n) && n !== existing)) {
          throw new Error(`Coefficient invalide : ${row[0]}, ${e.label}, ${p.label}. Aucune valeur importée.`);
        }
        if (n !== perfOf(result.ratios, u, p.key, e.key)) result.ratios = setPerf(result.ratios, u, p.key, e.key, n);
        result.coefficientsApplied += 1;
      }
    }
    if (ALL_USAGES.some(u => !seen.has(u.key))) {
      throw new Error(`Typologies manquantes dans Coefficients ${e.key}. Aucune valeur importée.`);
    }
  }
  return result;
}

const REFERENCE_FORMAT = 'plan-reference-ratios-v2';
const POST_SHEETS = { chauffage: 'Chauffage', ecs: 'ECS', froid: 'Froid' } as const;
const RATIO_HEADER = 'Ratio de référence (kWh/m²/an)';
function numberFormat(value: number, percent = false): string {
  const rounded = Math.round(value * 100) / 100;
  return `${Number.isInteger(rounded) ? '0' : '0.00'}${percent ? '" %"' : ''}`;
}

function referenceWorkbookError(sheet: string, address: string, message: string): never {
  throw new Error(`${sheet}!${address} : ${message}. Aucune valeur importée.`);
}

function workbookNumber(sheet: XLSX.WorkSheet, name: string, address: string, percent = false): number | null {
  const cell = sheet[address];
  if (cell?.f) referenceWorkbookError(name, address, 'saisissez un nombre, pas une formule Excel');
  if (!cell || cell.v === undefined || cell.v === null || (typeof cell.v === 'string' && !cell.v.trim())) return null;
  if (cell.t !== 'n' && cell.t !== 's') referenceWorkbookError(name, address, 'valeur numérique attendue');
  const raw = String(cell.v).trim();
  const textPercent = percent && raw.endsWith('%');
  const number = parseNum(textPercent ? raw.slice(0, -1) : raw);
  if (!Number.isFinite(number)) referenceWorkbookError(name, address, 'nombre invalide');
  // Excel stores 50% as 0.5, unlike the literal-percent format used by our template.
  const excelPercent = percent && cell.t === 'n' && /%/.test((cell.z ?? '').replace(/"[^"]*"|\\./g, ''));
  return excelPercent ? number * 100 : number;
}

function parseReferenceWorkbook(workbook: XLSX.WorkBook, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  const context = workbook.Sheets.Contexte;
  if (context.B1?.v !== REFERENCE_FORMAT) referenceWorkbookError('Contexte', 'B1', 'version de modèle inconnue');
  const schema = context.B2?.v;
  if (schema !== 'legacy' && schema !== 'climate2026') referenceWorkbookError('Contexte', 'B2', 'schéma de périodes inconnu');
  const zone: unknown = context.B3?.v;
  if (zone != null && zone !== '' && (typeof zone !== 'string' || !['IDF', 'H1a', 'H1b', 'H1c', 'H2a', 'H2b', 'H2c', 'H2d', 'H3'].includes(zone))) {
    referenceWorkbookError('Contexte', 'B3', 'zone climatique inconnue');
  }
  const parameters = workbook.Sheets['Paramètres'];
  if (!parameters) referenceWorkbookError('Paramètres', 'A1', 'feuille manquante');
  const hasCompactHeader = parameters.A1?.v === 'Paramètre' && parameters.B1?.v === 'Valeur à renseigner';
  const compact = hasCompactHeader || parameters.A1?.v === 'DJU de référence';
  const djuAddress = hasCompactHeader ? 'B2' : compact ? 'B1' : 'B5';
  const djclimAddress = hasCompactHeader ? 'B3' : compact ? 'B2' : 'B6';
  const djuLabelAddress = hasCompactHeader ? 'A2' : compact ? 'A1' : 'A5';
  const djclimLabelAddress = hasCompactHeader ? 'A3' : compact ? 'A2' : 'A6';
  if (parameters[djuLabelAddress]?.v !== 'DJU de référence' || parameters[djclimLabelAddress]?.v !== 'DJClim de référence') {
    referenceWorkbookError('Paramètres', djuLabelAddress, 'conservez les libellés DJU et DJClim du modèle');
  }
  if (!compact && (parameters.B8?.v ?? '') !== (zone ?? '')) {
    referenceWorkbookError('Paramètres', 'B8', 'la zone est une information de provenance, pas un sélecteur');
  }
  const dju = workbookNumber(parameters, 'Paramètres', djuAddress);
  const djclim = workbookNumber(parameters, 'Paramètres', djclimAddress);
  if (dju === null || !isValidDju(dju)) referenceWorkbookError('Paramètres', djuAddress, 'DJU attendu entre 500 et 5 000');
  if (djclim === null || !isValidDjclim(djclim)) referenceWorkbookError('Paramètres', djclimAddress, 'DJClim attendu entre 10 et 1 000');
  let ratios: RatioTable = { ...cloneRatios(base.ratios), periodSchema: schema, climateZone: zone || undefined };
  const periods = periodsOf(ratios);
  const reference = referencePeriodOf(ratios);
  const performance = perfTableOf(ratios);
  for (const usage of ALL_USAGES) for (const period of periods) {
    ratios[usage.key][period.key] = { chauffage: null, ecs: null, froid: null };
    const percent = period.key === reference ? 100 : null;
    performance[usage.key][period.key] = { chauffage: percent, ecs: percent, froid: percent };
  }
  ratios.performance = performance;
  let applied = 0;
  let coefficientsApplied = 0;
  for (const energy of ENERGIES) {
    const name = POST_SHEETS[energy.key];
    const sheet = workbook.Sheets[name];
    if (!sheet) referenceWorkbookError(name, 'A1', 'les trois feuilles Chauffage, ECS et Froid sont requises');
    const expected = ['Typologie', RATIO_HEADER, ...periods.map(p => `${p.label} (%)`)];
    const headerRow = sheet.A1?.v === 'Typologie' ? 0 : 3;
    for (const [col, label] of expected.entries()) {
      const address = XLSX.utils.encode_cell({ r: headerRow, c: col });
      if (sheet[address]?.v !== label) referenceWorkbookError(name, address, `colonne attendue : ${label}`);
    }
    const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1');
    if (range.e.r > 100 || range.e.c >= expected.length) referenceWorkbookError(name, 'A4', 'dimensions du tableau invalides');
    const seen = new Set<string>();
    for (let row = headerRow + 1; row <= range.e.r; row += 1) {
      const address = (col: number) => XLSX.utils.encode_cell({ r: row, c: col });
      const label = sheet[address(0)]?.v;
      if (label == null && expected.every((_, col) => sheet[address(col)]?.v == null && !sheet[address(col)]?.f)) continue;
      const usage = typeof label === 'string' ? USAGE_BY_LABEL.get(norm(label)) : undefined;
      if (!usage || seen.has(usage)) referenceWorkbookError(name, address(0), 'typologie inconnue ou dupliquée');
      seen.add(usage);
      const ratio = workbookNumber(sheet, name, address(1));
      if (ratio !== null) {
        if (!isValidRatio(ratio)) referenceWorkbookError(name, address(1), 'ratio attendu entre 0 et 1 000 kWh/m²/an');
        ratios = setUsageRatio(ratios, usage, energy.key, ratio);
        applied += 1;
      }
      for (const [col, period] of periods.entries()) {
        const cellAddress = address(col + 2);
        const percent = workbookNumber(sheet, name, cellAddress, true);
        if (period.key === reference) {
          if (percent !== 100) referenceWorkbookError(name, cellAddress, 'la période de référence doit rester à 100 %');
        } else if (percent !== null) {
          const existing = base.ratios.periodSchema === schema || (schema === 'legacy' && !base.ratios.periodSchema)
            ? perfOf(base.ratios, usage, period.key, energy.key) : null;
          if (!isValidPerf(percent) && percent !== existing) referenceWorkbookError(name, cellAddress, 'coefficient attendu entre 1 et 100 %');
          ratios = setPerf(ratios, usage, period.key, energy.key, percent);
        }
        if (percent !== null) coefficientsApplied += 1;
      }
    }
    if (USAGES.some(usage => !seen.has(usage.key))) referenceWorkbookError(name, `A${headerRow + 2}`, 'les onze typologies sont requises');
  }
  return { degrees: { dju, djclim }, ratios, applied, coefficientsApplied, skipped: [], djuFound: true, djclimFound: true };
}

/** Four editable sheets mirror the reference inputs; hidden context preserves the period contract. */
export function ratioTemplate(degrees: DegreeDays, ratios: RatioTable): XLSX.WorkBook {
  const periods = periodsOf(ratios);
  const workbook = XLSX.utils.book_new();
  const parameters = XLSX.utils.aoa_to_sheet([
    ['Paramètre', 'Valeur à renseigner'],
    ['DJU de référence', degrees.dju],
    ['DJClim de référence', degrees.djclim],
  ]);
  parameters['!cols'] = [{ wch: 28 }, { wch: 24 }];
  for (const address of ['B2', 'B3']) parameters[address].z = numberFormat(parameters[address].v);
  XLSX.utils.book_append_sheet(workbook, parameters, 'Paramètres');
  const usages = ALL_USAGES.filter(usage => USAGES.some(u => u.key === usage.key)
    || periods.some(period => ENERGIES.some(energy => ratios[usage.key][period.key][energy.key] !== null
      || (period.key !== referencePeriodOf(ratios) && perfOf(ratios, usage.key, period.key, energy.key) !== null))));
  for (const energy of ENERGIES) {
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Typologie', RATIO_HEADER, ...periods.map(p => `${p.label} (%)`)],
      ...usages.map(u => [u.label, usageRatio(ratios, u.key)[energy.key], ...periods.map(p => perfOf(ratios, u.key, p.key, energy.key))]),
    ]);
    sheet['!cols'] = [{ wch: 55 }, { wch: 36 }, ...periods.map(() => ({ wch: 32 }))];
    sheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(periods.length + 1)}${1 + usages.length}` };
    for (let row = 1; row < 1 + usages.length; row += 1) for (let col = 1; col <= periods.length + 1; col += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell?.t === 'n') cell.z = numberFormat(cell.v, col !== 1);
    }
    XLSX.utils.book_append_sheet(workbook, sheet, POST_SHEETS[energy.key]);
  }
  const context = XLSX.utils.aoa_to_sheet([
    ['Format', REFERENCE_FORMAT],
    ['Périodes', ratios.periodSchema ?? 'legacy'],
    ['Zone climatique', ratios.climateZone ?? ''],
  ]);
  XLSX.utils.book_append_sheet(workbook, context, 'Contexte');
  workbook.Workbook = { Sheets: workbook.SheetNames.map(name => ({ name, Hidden: name === 'Contexte' ? 1 : 0 })) };
  return workbook;
}

export function downloadRatioTemplate(degrees: DegreeDays, ratios: RatioTable): void {
  XLSX.writeFile(ratioTemplate(degrees, ratios), 'template_ratio.xlsx', { compression: true });
}
